import React from 'react';
import { CheckCircle2, ExternalLink, Mail } from 'lucide-react';
import PageHeader from '../components/PageHeader';

const steps = [
  {
    title: 'Install the app from HighLevel',
    body: 'An agency administrator installs 4D EMR for the intended sub-account and approves the requested permissions. After authorization, HighLevel returns you to the ZoneDM dashboard.',
  },
  {
    title: 'Connect your 4D EMR account',
    body: 'Sign in to ZoneDM, open Integrations, and enter the API credentials supplied for your active 4D EMR account. Credentials are used only to establish the integration.',
  },
  {
    title: 'Choose what to synchronize',
    body: 'Map the HighLevel pipeline, stages, calendars, tags, and custom fields used by your practice. Confirm which patient, appointment, provider, procedure, quote, and opportunity records should sync.',
  },
  {
    title: 'Run and verify the first sync',
    body: 'Start a small initial sync, confirm the resulting records in both systems, and review Sync Logs for any items that need attention. Stored source identifiers help prevent duplicate records.',
  },
];

export default function GettingStartedPage() {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <PageHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-200 sm:p-10">
          <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600">4D EMR by ZoneDM</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Getting started</h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-gray-600">
            Follow these steps to connect a HighLevel sub-account to 4D EMR and verify the first synchronization.
          </p>

          <div className="mt-10 space-y-8">
            {steps.map((step, index) => (
              <section key={step.title} className="flex gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white">
                  {index + 1}
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{step.title}</h2>
                  <p className="mt-2 leading-7 text-gray-600">{step.body}</p>
                </div>
              </section>
            ))}
          </div>

          <section className="mt-10 rounded-xl bg-indigo-50 p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-indigo-600" />
              <div>
                <h2 className="font-semibold">Before you begin</h2>
                <p className="mt-1 text-gray-700">
                  You need an active 4D EMR account with API credentials and agency-level permission to install apps for the target HighLevel sub-account.
                </p>
              </div>
            </div>
          </section>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href="/api"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 font-medium text-white hover:bg-indigo-700"
            >
              View API documentation <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href="mailto:jennifer@zonedm.com"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              Contact support <Mail className="h-4 w-4" />
            </a>
          </div>

          <p className="mt-8 text-sm text-gray-500">
            To disconnect the integration, remove the app in HighLevel. ZoneDM will deactivate the connection and clear its stored HighLevel authorization tokens.
          </p>
        </div>
      </main>
    </div>
  );
}
